// =============================================================================
// deliveryWriteDrop.js — the FOH courier hand-off WRITE-DROP gate, and the till's
// Supabase "Today's orders" read (Lane BM, 13 Sep 2026).
//
// WHAT THIS IS FOR. Every courier hand-off photographed at a till is written to
// two places today: Google (Apps Script `uploadDeliveryOrderPhoto` +
// `deliveryOrderLogged`) and Supabase (the `record_courier_handoff` door, 0600,
// recording on production since 19 Aug 2026 — as a fire-and-forget shadow beside
// the Google write). Google is still the boss. Deliveries can never count as off
// Google until a till can STOP writing to Google, one shop at a time, with the
// Supabase write becoming the one that must succeed. This file is that switch,
// plus the read the "Today's orders" strip needs once Google is no longer filled.
//
// SHIPS EMPTY. DELIVERY_WRITE_DROP_HOSTS holds no host. Nothing changes on any
// tablet until a reviewed one-line host add — and only after the pre-removal gate
// in REMOVAL_PACK_DELIVERY_WRITES.md §(c) is green: both Command Centre delivery
// screens flipped + soaked, the Apps Script write-door counter at zero for the two
// actions for ≥14 days with the counter proven alive, and a verified backup.
// Runbook: RUNBOOK_DELIVERY_WRITE_DROP.md.
//
// WHEN ON for a host (src/foh.jsx, the Delivery Orders tile):
//   * submit() skips BOTH Apps Script POSTs and AWAITS
//     deliveryOrdersSync.recordCourierHandoff() with requireAllPhotos. A failure
//     blocks "saved" and shows a retry message (handoffFailureMessage); the form
//     is kept so the retry re-uses the same photos, keys and token. A hand-off
//     whose photos did not ALL reach the bucket is refused before any row is
//     written (a row can never have a photo added to it later — the door is
//     on-conflict-do-nothing — and the tablet holds the only copy of a fresh
//     camera shot). Nothing is swallowed.
//   * the "Today's orders" strip reads delivery_orders_today (migration 1240) —
//     a per-shop, today-only, five-field feed (no `note` — narrowed 14 Sep 2026,
//     external review of PR #343, finding 1: the strip never renders it) —
//     instead of Google's `getdeliveryorderstoday`. Feed rows carry no photo link
//     (the photos live in a private bucket behind the cc_admin signer), so the
//     strip shows a "photo saved" chip for them; the rows THIS till logged keep
//     their local thumbnails for the rest of the session (mergeLocalThumbnails).
//     Since Lane CM (16 Sep 2026) that read WAITS for the tablet's own anonymous
//     Supabase session before it asks: migration 1580 took the no-login grant off
//     the feed, so a caller holding nothing but the public key is now refused.
// WHEN OFF (every host today): the tile runs its original code, byte for byte —
// nothing in this file is consulted for behaviour.
//
// `?deliverywritedrop=off` ALWAYS wins per tab (an instant kill).
// `?deliverywritedrop=on` forces it for a try-first on ONE tablet.
// A listed/forced host is still REFUSED unless both preconditions hold:
//   1. the Supabase hand-off write is switched on (DELIVERY_ORDERS_SYNC_ENABLED);
//   2. the host dual-runs (dualRunEnabled) — recordCourierHandoff returns a quiet
//      "skipped" on a non-dual-run host, so dropping Google there would record
//      the hand-off NOWHERE.
// When refused it says why, once, and stays dual-write with Google.
// arndale is CLOSED (16 Aug 2026) and must never be listed.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled } from "./stockSync.js";
import { ensureAnonSession, boundedSignal } from "./auth.js";
import { DELIVERY_ORDERS_SYNC_ENABLED } from "./deliveryOrdersSync.js";

export const DELIVERY_WRITE_DROP_HOSTS = new Set([
  /* empty until go-live — a reviewed one-line add, one FOH host at a time:
     "trafford.eatrolled.com", "meadowhall.eatrolled.com"
     — never arndale (closed 16 Aug 2026); never the kitchen (inbound deliveries
     ride Lane BE's STOCK_WRITE_DROP_HOSTS instead); never bare site keys. */
]);

// The feed the strip reads on a drop host (migration 1240). One argument.
export const TODAY_FEED = "delivery_orders_today";

// Parse the ?deliverywritedrop override: "on" | "off" | null. Pure (search
// injectable for tests; falls back to window.location.search in the browser).
export function deliveryWriteDropOverride(search) {
  try {
    const s = search != null ? search : (typeof window !== "undefined" ? window.location.search : "");
    const q = new URLSearchParams(s).get("deliverywritedrop");
    if (q === "on" || q === "off") return q;
  } catch (e) {}
  return null;
}

// The PURE decision — every input explicit, no globals, so the whole truth table
// is unit-tested with no window. `off` always wins; a listed/forced host still
// needs BOTH preconditions. Returns true only when the Google write may be dropped.
export function decideDeliveryWriteDrop({ override = null, hostListed = false, syncEnabled = false, dualRun = false } = {}) {
  if (override === "off") return false;              // instant kill — always wins
  const wanted = override === "on" || hostListed;   // the reviewed go-live switch
  if (!wanted) return false;
  if (!syncEnabled) return false;                   // the Supabase write must be switched on
  if (!dualRun) return false;                       // the host must dual-run, or the write is skipped
  return true;
}

// Runtime combiner the tile calls. Reads the host + override + the two
// preconditions, and warns ONCE when a listed/forced host can't yet drop.
let _refusalWarned = false;
export function deliveryWriteDropEnabled(search) {
  let host = "";
  if (typeof window !== "undefined") host = (window.location.hostname || "").toLowerCase();
  const override = deliveryWriteDropOverride(search);
  const hostListed = DELIVERY_WRITE_DROP_HOSTS.has(host);
  const syncEnabled = DELIVERY_ORDERS_SYNC_ENABLED === true;
  const dualRun = dualRunEnabled();
  const on = decideDeliveryWriteDrop({ override, hostListed, syncEnabled, dualRun });
  if (!on && (override === "on" || hostListed) && !_refusalWarned) {
    _refusalWarned = true;
    console.warn(
      "[delivery-write-drop] REFUSED (staying dual-write with Google): sync-enabled=" + syncEnabled +
      " dual-run=" + dualRun + " — a host must have the Supabase hand-off write switched on AND dual-run before its Google write is dropped."
    );
  }
  return on;
}

// ---- the strip's read --------------------------------------------------------
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const tsOf = (iso) => { const t = Date.parse(iso); return Number.isFinite(t) ? t : Date.now(); };

// PURE: the feed's rows (a jsonb array, or its JSON text) -> the strip's row
// shape. photoUrls is ALWAYS empty (the feed never carries a photo link);
// photoCount says how many pictures exist, so the strip can show a "photo saved"
// chip instead of a broken image. `note` is deliberately NOT carried through
// (external review, 14 Sep 2026, finding 1): migration 1240 no longer returns
// it, and even if a stray `note` ever showed up on the payload this shaper
// still would not propagate it — the strip has nowhere that renders it for a
// feed-sourced row.
export function shapeTodayRows(data, siteKey = "") {
  let arr = data;
  if (typeof arr === "string") { try { arr = JSON.parse(arr); } catch (e) { arr = null; } }
  if (!Array.isArray(arr)) return [];
  return arr.filter((r) => r && typeof r === "object").map((r) => ({
    ts: tsOf(r.occurred_at),
    orderNo: String(r.order_no == null ? "" : r.order_no),
    platform: String(r.platform == null ? "" : r.platform),
    staffName: String(r.staff_name == null ? "" : r.staff_name),
    photoUrls: [],
    photoCount: Math.max(0, Math.floor(num(r.photo_count))),
    site: siteKey || "",
    _fromSupabase: true,
  }));
}

// PURE: when the feed refresh replaces the list, keep the LOCAL thumbnails this
// till attached to the rows it logged (matched by order number) — the staff
// member keeps seeing what they photographed, even though the feed itself never
// carries a link. A feed row that already has links is left alone.
export function mergeLocalThumbnails(feedRows, prevRows) {
  const local = new Map();
  for (const p of (Array.isArray(prevRows) ? prevRows : [])) {
    if (p && p.orderNo && Array.isArray(p.photoUrls) && p.photoUrls.length) local.set(String(p.orderNo), p.photoUrls);
  }
  return (Array.isArray(feedRows) ? feedRows : []).map((r) => {
    if (!r || (Array.isArray(r.photoUrls) && r.photoUrls.length)) return r;
    const urls = local.get(String(r.orderNo));
    return urls ? { ...r, photoUrls: urls, photoUrl: urls[0] } : r;
  });
}

// How long the strip will wait for a SIGN-IN before giving up and saying so:
// eight seconds, then an honest message instead of a spinner. Exported so the
// unit test can state the number it relies on.
//
// Read that narrowly — it bounds the SIGN-IN step and nothing else.
//
// `timeoutMs` follows boundedSignal's convention: zero or less means NO bound at
// all, not "don't wait". Nothing in the app passes it; the only caller
// (src/foh.jsx) passes no options and gets the eight seconds.
export const TODAY_SESSION_TIMEOUT_MS = 8000;

// How long the strip will wait for the FEED CALL itself, separately (17 Sep 2026,
// outside senior review of PR #376). Until now this call carried no timeout at
// all, so a connection that accepts and then never answers — a shopping-centre
// captive portal is the everyday version — left the strip on "Loading…" for ever
// (measured in the refute pass of PR #376: a feed call that never answers was
// still pending after nine seconds). The staff member had no way to tell a slow
// wifi from a broken one.
//
// It is a SEPARATE budget on purpose, not a share of the eight seconds above. One
// shared budget would let a slow sign-in eat the feed's time and abort a call that
// was about to succeed — a new way to fail that the Google fetch it replaces never
// had. Ten seconds because the feed is the useful half: if the sign-in took seven,
// the read still gets its own full ten before anyone is told to check the wifi.
export const TODAY_FEED_TIMEOUT_MS = 10000;

// The read the strip makes on a drop host. Never throws and never rejects:
// returns { ok, rows, error }. `rpc` and `ensure` are injectable so the tests
// never touch the network; in the browser they are the shared Supabase client
// and the tablet's own anonymous sign-in.
//
// LANE CM (16 Sep 2026) — WHY THE SESSION COMES FIRST. This feed used to be
// answerable with no login at all: anyone holding the public key that ships in
// every tablet's bundle could ask "what did shop X hand to couriers today?".
// Migration 1580 takes that away, so from now on the strip must be signed in
// (an anonymous Supabase session — the same one the kitchen doorbell and the
// compliance readers already wait for) BEFORE it asks. `persistSession` is
// false on the kiosk client, so a fresh page load genuinely has no session and
// this await is what creates one. If it cannot be got — dead wifi, the auth
// service down — the strip says so honestly rather than making a call that can
// only be refused.
//
// 17 SEP 2026 — THE FEED CALL IS BOUNDED TOO, ON ITS OWN CLOCK. Both steps now
// have a time limit and neither can spend the other's (see TODAY_FEED_TIMEOUT_MS).
// The real Supabase call is handed the timeout's abort signal, so the fetch itself
// is cancelled rather than left running in the background; an injected `rpc` that
// ignores the signal is still bounded, because the wait is raced as well.
export async function readTodayOrders(siteKey, { rpc, ensure, timeoutMs = TODAY_SESSION_TIMEOUT_MS, feedTimeoutMs = TODAY_FEED_TIMEOUT_MS } = {}) {
  const call = rpc || ((fn, args, opts) => {
    const q = supabase.rpc(fn, args);
    return opts && opts.signal ? q.abortSignal(opts.signal) : q;
  });
  const getSession = ensure || ensureAnonSession;
  const bound = boundedSignal(null, timeoutMs > 0 ? timeoutMs : 0);
  // Started AFTER the sign-in returns, never here — a clock created up here would
  // already be running while the sign-in took its time, which is the shared budget
  // this change exists to avoid.
  let feedBound = null;
  try {
    // Bounded so a wifi that never answers cannot hang the strip. The promise is
    // pre-handled so a late rejection after the timeout wins can never surface as
    // an unhandled rejection.
    let session = null;
    try {
      const p = Promise.resolve().then(() => getSession());
      p.catch(() => {});
      session = await bound.race(p);
    } catch (e) {
      console.error("[delivery-write-drop] no Supabase session for the today feed:", e?.message || e);
      return { ok: false, rows: [], error: "Could not load today's orders — check the wifi" };
    }
    if (!session) {
      console.error("[delivery-write-drop] no Supabase session for the today feed: the sign-in returned nothing");
      return { ok: false, rows: [], error: "Could not load today's orders — check the wifi" };
    }

    // The feed call, on its own clock, started HERE — the sign-in's time is spent
    // and none of it comes out of the feed's ten seconds. Same shape as the
    // sign-in above: the promise is pre-handled so a late rejection after the
    // timeout has won can never surface as an unhandled rejection in the console.
    feedBound = boundedSignal(null, feedTimeoutMs > 0 ? feedTimeoutMs : 0);
    let res;
    try {
      const q = Promise.resolve().then(() => call(TODAY_FEED, { p_site: siteKey }, { signal: feedBound.signal }));
      q.catch(() => {});
      res = (await feedBound.race(q)) || {};
    } catch (e) {
      console.error(
        feedBound.timedOut()
          ? "[delivery-write-drop] today feed gave no answer within " + feedTimeoutMs + "ms:"
          : "[delivery-write-drop] today feed threw:",
        e?.message || e
      );
      return { ok: false, rows: [], error: "Could not load today's orders — check the wifi" };
    }
    const { data, error } = res;
    if (error) {
      console.error("[delivery-write-drop] today feed refused:", error.message || error);
      return { ok: false, rows: [], error: "Could not load today's orders — try again in a moment" };
    }
    return { ok: true, rows: shapeTodayRows(data, siteKey), error: null };
  } catch (e) {
    console.error("[delivery-write-drop] today feed threw:", e?.message || e);
    return { ok: false, rows: [], error: "Could not load today's orders — check the wifi" };
  } finally {
    bound.release();                          // clear both timers either way, so
    if (feedBound) feedBound.release();        // nothing is left ticking
  }
}

// ---- honest messages for the awaited write -----------------------------------
// What the till shows when the awaited Supabase write did NOT land. Plain words,
// always says "NOT saved", never a quiet success.
export function handoffFailureMessage(res) {
  if (res && res.reason === "door-missing") {
    return "NOT saved — the hand-off recorder is missing on this project. Tell Adam.";
  }
  if (res && res.reason === "photo-short") {
    const p = res.photos || {};
    const attempted = Number(p.attempted) || 0;
    const missing = Math.max(1, attempted - (Number(p.landed) || 0));
    return "NOT saved — " + missing + " of " + attempted + " photo" + (attempted === 1 ? "" : "s") +
      " did not upload, so nothing was recorded. Your photos are still here: check the wifi and tap Confirm again. If it keeps failing, tell Adam.";
  }
  if (res && res.skipped) {
    return "NOT saved — hand-off recording is switched off on this tablet, so it had nowhere to go. Tell Adam.";
  }
  const err = res && res.error;
  const msg = typeof err === "string" ? err : String((err && err.message) || "");
  if (/no pass|no pin/i.test(msg)) return "NOT saved — the staff PIN didn't verify. Try again.";
  if (/failed to fetch|network|timeout|load failed|econn/i.test(msg)) return "NOT saved — couldn't reach the server. Check the wifi and try again.";
  return "NOT saved — the server refused the hand-off" + (msg ? " (" + msg + ")" : "") + ". Try again; if it keeps failing, tell Adam.";
}
